import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ALLE_CONTACTEN_API, LEVERANCIERS_API, PERSONEEL_API, VRIJWILLIGERS_API } from "../lib/contactApiPaths";
import type { Contact, ContactEntityKind, Group, MailerLiteListConnectionSummary, User } from "@domaddress/shared";
import {
  canViewActivityLog,
  hasOrgWideContactDirectoryAccess,
  hasOrgWideContactReadAccess,
  isOrgContactReadOnlyRole,
} from "@domaddress/shared";
import ContactCard from "../components/ContactCard";
import ContactListRow from "../components/ContactListRow";
import ContactFormModal from "../components/ContactFormModal";
import ContactViewModal from "../components/ContactViewModal";
import ViewModeToggle from "../components/ViewModeToggle";
import PaginationBar from "../components/PaginationBar";
import { useViewMode } from "../hooks/useViewMode";
import {
  useVcardEmailSend,
  VcardEmailToolbarButton,
  VcardEmailAside,
} from "../components/VcardEmailSendParts";
import MailerLiteConnectModal from "../components/MailerLiteConnectModal";

interface Props {
  /** Ingelogde gebruiker (voor alleen-lezen rollen: geen bewerken/importeren). */
  user?: User | null;
  apiPath?: string;
  title?: string;
  singularLabel?: string;
  pluralLabel?: string;
  importPath?: string;
  pageKey?: string;
}

/** Korte label voor `entity_kind` in de “alle contacten”-lijst. */
function primaryListBadgeLabel(entityKind: string | null | undefined): string {
  switch (entityKind) {
    case "vrijwilliger":
      return "Vrijwilliger";
    case "personeel":
      return "Personeel";
    case "dominicusganger":
      return "Dominicusganger";
    case "extern":
      return "Extern";
    case "leverancier":
      return "Leverancier";
    default:
      return entityKind || "—";
  }
}

export default function ContactsPage({
  user = null,
  apiPath = VRIJWILLIGERS_API,
  title = "Vrijwilligers",
  singularLabel = "vrijwilliger",
  pluralLabel = "vrijwilligers",
  importPath = "/vrijwilligers/importeren",
  pageKey = "vrijwilligers",
}: Props) {
  const readOnly = user ? isOrgContactReadOnlyRole(user) : false;
  const canMailerLiteConnect = !!user && canViewActivityLog(user);
  const entityKindForMailerLite: ContactEntityKind | null =
    apiPath === VRIJWILLIGERS_API
      ? "vrijwilliger"
      : apiPath === PERSONEEL_API
        ? "personeel"
        : apiPath === LEVERANCIERS_API
          ? "leverancier"
          : apiPath === ALLE_CONTACTEN_API
            ? null
            : null;
  const isAlleContacten = apiPath === ALLE_CONTACTEN_API;
  const isStaffAddressList = apiPath === VRIJWILLIGERS_API || apiPath === PERSONEEL_API;
  const canFilterNoGroupActivity =
    !!user && (hasOrgWideContactDirectoryAccess(user) || hasOrgWideContactReadAccess(user));
  const canBulkDemoteVrijwilligers =
    apiPath === VRIJWILLIGERS_API && !!user && hasOrgWideContactDirectoryAccess(user) && !readOnly;
  const canDemoteAllWithoutGroup =
    apiPath === VRIJWILLIGERS_API && !!user?.is_super_admin && !readOnly;
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useViewMode(pageKey);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  /** Alleen GET /api/contacts: filter op primaire kaart-soort. */
  const [entityKindFilter, setEntityKindFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [viewing, setViewing] = useState<Contact | null>(null);
  const [groupFilter, setGroupFilter] = useState("");
  const [groups, setGroups] = useState<Group[]>([]);
  /** Alleen vrijwilligers/personeel + org-rechten: filter op groepsrollen. */
  const [staffGroupActivityFilter, setStaffGroupActivityFilter] = useState<"all" | "none" | "with_role">("all");
  /** Filter op bekend e-mailadres (dominicus → privé → werk). */
  const [emailFilter, setEmailFilter] = useState<"" | "without" | "with">("");
  const [demoteBusy, setDemoteBusy] = useState(false);
  const [demoteAllOpen, setDemoteAllOpen] = useState(false);
  const [demoteAllPreview, setDemoteAllPreview] = useState<
    { id: number; display_name: string }[]
  >([]);
  const [demoteAllLoading, setDemoteAllLoading] = useState(false);
  const [mlConnectOpen, setMlConnectOpen] = useState(false);
  const [mlConnectMsg, setMlConnectMsg] = useState<string | null>(null);
  const [mlListConnections, setMlListConnections] = useState<MailerLiteListConnectionSummary[]>([]);
  const [mlResyncBusy, setMlResyncBusy] = useState(false);

  /** entity_kind used to look up MailerLite list connections (fixed list or filter on alle-contacten). */
  const listEntityKindForMailerLite = entityKindForMailerLite ?? (entityKindFilter || null);

  const vcard = useVcardEmailSend(apiPath);

  useEffect(() => {
    api.get("/api/groups").then((r) => setGroups(r.data.data));
  }, []);

  const fetchMailerLiteListConnections = useCallback(async () => {
    if (!user || !listEntityKindForMailerLite) {
      setMlListConnections([]);
      return;
    }
    try {
      const res = await api.get<{ data: MailerLiteListConnectionSummary[] }>(
        `/api/mailerlite/list-connections?entity_kind=${encodeURIComponent(listEntityKindForMailerLite)}`
      );
      setMlListConnections(res.data.data ?? []);
    } catch {
      setMlListConnections([]);
    }
  }, [user, listEntityKindForMailerLite]);

  useEffect(() => {
    void fetchMailerLiteListConnections();
  }, [fetchMailerLiteListConnections]);

  async function resyncMailerLiteList() {
    if (mlListConnections.length === 0) return;
    if (
      !confirm(
        `Contacten opnieuw synchroniseren naar MailerLite (${mlListConnections.map((c) => c.ml_group_name).join(", ")})? Dit kan even duren.`
      )
    ) {
      return;
    }
    setMlResyncBusy(true);
    setMlConnectMsg(null);
    try {
      let synced = 0;
      let skipped = 0;
      let failed = 0;
      for (const conn of mlListConnections) {
        const res = await api.post<{
          data: { examined: number; synced: number; skipped: number; failed: number };
        }>(`/api/mailerlite/connections/${conn.id}/push`);
        synced += res.data.data.synced;
        skipped += res.data.data.skipped;
        failed += res.data.data.failed;
      }
      setMlConnectMsg(
        `MailerLite sync voltooid: ${synced} gesynchroniseerd, ${skipped} overgeslagen${failed > 0 ? `, ${failed} mislukt` : ""}.`
      );
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setMlConnectMsg(ax.response?.data?.error ?? "Synchroniseren mislukt");
    } finally {
      setMlResyncBusy(false);
    }
  }

  useEffect(() => {
    setPage(1);
  }, [search, groupFilter, cityFilter, countryFilter, entityKindFilter, perPage, staffGroupActivityFilter, emailFilter]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(page),
        per_page: String(perPage),
      };
      if (search.trim()) params.search = search.trim();
      if (groupFilter) params.group_id = groupFilter;
      if (cityFilter.trim()) params.city = cityFilter.trim();
      if (countryFilter.trim()) params.country = countryFilter.trim();
      if (isAlleContacten && entityKindFilter) params.entity_kind = entityKindFilter;
      if (isStaffAddressList && canFilterNoGroupActivity && staffGroupActivityFilter !== "all") {
        params.group_activity = staffGroupActivityFilter;
      }
      if (emailFilter) params.has_email = emailFilter;
      const res = await api.get(apiPath, { params });
      setContacts(res.data.data);
      setTotal(typeof res.data.total === "number" ? res.data.total : res.data.data.length);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    perPage,
    search,
    groupFilter,
    cityFilter,
    countryFilter,
    entityKindFilter,
    isAlleContacten,
    apiPath,
    isStaffAddressList,
    staffGroupActivityFilter,
    canFilterNoGroupActivity,
    emailFilter,
  ]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  /** Open een kaart vanuit een deeplink (?contactId=…) bv. activiteitenlog. */
  useEffect(() => {
    const raw = searchParams.get("contactId");
    const id = raw != null && raw !== "" ? Number.parseInt(raw, 10) : NaN;
    if (!Number.isInteger(id) || id < 1) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<{ data: Contact }>(`${apiPath}/${id}`);
        if (!cancelled) setViewing(res.data.data);
      } catch {
        if (!cancelled) setViewing(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, apiPath]);

  async function handleDelete(id: number) {
    if (!confirm(`Dit contact verwijderen?`)) return;
    await api.delete(`${apiPath}/${id}`);
    await fetchContacts();
  }

  async function openDemoteAllModal() {
    setDemoteAllLoading(true);
    setDemoteAllOpen(true);
    try {
      const res = await api.get<{
        data: { id: number; display_name: string }[];
        count: number;
      }>("/api/admin/vrijwilligers/zonder-groep");
      setDemoteAllPreview(res.data.data ?? []);
    } catch {
      setDemoteAllPreview([]);
      alert("Kon de lijst niet laden.");
      setDemoteAllOpen(false);
    } finally {
      setDemoteAllLoading(false);
    }
  }

  async function handleDemoteAllWithoutGroup() {
    if (!confirm(`Weet je zeker dat je ${demoteAllPreview.length} personen naar dominicusgangers wilt verplaatsen?`)) {
      return;
    }
    setDemoteBusy(true);
    try {
      const res = await api.post<{ updated_count: number; skipped: { id: number; reason: string }[] }>(
        "/api/admin/vrijwilligers/demote-zonder-groep"
      );
      alert(`Klaar: ${res.data.updated_count ?? 0} verwerkt.`);
      setDemoteAllOpen(false);
      await fetchContacts();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      alert(ax.response?.data?.error ?? "Actie mislukt");
    } finally {
      setDemoteBusy(false);
    }
  }

  async function handleDemoteCurrentPageWithoutRoles() {
    const noGroupRole = contacts.filter((x) => x.group_activity && !x.group_activity.has_any_group_role);
    const ids = noGroupRole.map((x) => x.id);

    if (ids.length === 0) {
      alert("Op deze pagina staan geen personen zonder groepsrol.");
      return;
    }

    const msg =
      `We verwerken ${ids.length} kaart(en) van deze pagina: primair vrijwilliger wordt dominicusganger; ` +
      `bij zowel vrijwilliger- als dominicusganger-label wordt het type dominicusganger; ` +
      `met alleen een vrijwilliger-label (bijv. bij personeel) wordt dat label verwijderd. ` +
      `Alleen personen zonder groepsrol. Doorgaan?`;
    if (!confirm(msg)) return;

    setDemoteBusy(true);
    try {
      const res = await api.post<{
        ok: boolean;
        updated_count: number;
        skipped: { id: number; reason: string }[];
      }>("/api/admin/vrijwilligers/demote-to-dominicusganger", { contact_ids: ids });
      const skipped = res.data.skipped ?? [];
      const byReason: Record<string, number> = {};
      for (const s of skipped) {
        byReason[s.reason] = (byReason[s.reason] ?? 0) + 1;
      }
      const reasonNl: Record<string, string> = {
        not_vrijwilliger: "stond niet (meer) op de vrijwilligerslijst",
        not_on_vrijwilligers_list: "geen vrijwilliger-label en geen primair vrijwilliger",
        has_group_roles: "had alsnog een groepsrol (gegevens kunnen intussen gewijzigd zijn)",
        not_found: "niet gevonden",
      };
      const detail =
        skipped.length > 0
          ? ` ${skipped.length} overgeslagen: ${Object.entries(byReason)
              .map(([k, n]) => `${n}× ${reasonNl[k] ?? k}`)
              .join("; ")}.`
          : "";
      alert(`Klaar: ${res.data.updated_count ?? 0} kaart(en) verwerkt (dominicusganger en/of label).${detail}`);
      await fetchContacts();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      alert(ax.response?.data?.error ?? ax.message ?? "Actie mislukt");
    } finally {
      setDemoteBusy(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageOutOfRange = page > totalPages && total > 0;
  useEffect(() => {
    if (pageOutOfRange) setPage(totalPages);
  }, [pageOutOfRange, totalPages, page]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div
            className="page-title-row"
            style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.15rem" }}
          >
            <h2 className="page-title" style={{ margin: 0 }}>
              {title}
            </h2>
            {mlListConnections.length > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
                <span className="group-badge group-badge--mailerlite" title={mlListConnections.map((c) => c.ml_group_name).join(", ")}>
                  mailerlite
                </span>
                {canMailerLiteConnect && (
                  <button
                    type="button"
                    className="btn-secondary mailerlite-list-sync-btn"
                    disabled={mlResyncBusy}
                    title="Opnieuw synchroniseren naar MailerLite"
                    onClick={() => void resyncMailerLiteList()}
                  >
                    {mlResyncBusy ? "…" : "↻ sync"}
                  </button>
                )}
              </span>
            )}
          </div>
          <p className="page-subtitle">
            {loading ? "…" : `${total} ${total !== 1 ? pluralLabel : singularLabel}`}
            {isAlleContacten && (
              <span
                className="data-list-row-muted"
                style={{ display: "block", marginTop: "0.35rem", fontSize: "0.9rem", maxWidth: "42rem" }}
              >
                Alle adresboektypen samen. Filter op kaarttype, groep, plaats of land; open een kaart om details en
                relaties te bekijken.
              </span>
            )}
            {isStaffAddressList && (
              <span
                className="data-list-row-muted"
                style={{ display: "block", marginTop: "0.35rem", fontSize: "0.9rem", maxWidth: "48rem" }}
              >
                Bij elke kaart zie je of iemand <strong>lid</strong>, <strong>voorzitter</strong>,{" "}
                <strong>groepsbeheerder</strong> of <strong>kijker</strong> is in minstens één groep — of{" "}
                <strong>geen groepsrol</strong>. Hierboven kun je daarop filteren. Een kaart kan óók op deze lijst
                staan via een <strong>label</strong> “vrijwilliger”. Met de knop “Dominicusgangers” zet je primair
                vrijwilliger om naar dominicusganger; bij labels vrijwilliger+dominicusganger wordt dominicusganger
                primair; met alleen het vrijwilliger-label wordt dat label verwijderd.
              </span>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {user && <VcardEmailToolbarButton b={vcard} />}
          {canMailerLiteConnect && (
            <button type="button" className="btn-secondary" onClick={() => setMlConnectOpen(true)}>
              MailerLite koppelen
            </button>
          )}
          {!readOnly && importPath && (
            <Link to={importPath} className="btn-secondary">
              <span>⇪</span> Importeren
            </Link>
          )}
          {!readOnly && (
            <button className="btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
              <span>+</span> Nieuw
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" as const }}>
        <div style={{ flex: 1, minWidth: 0 }}>
      <div className="filters">
        <div className="filters-start">
          <div className="search-box">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Zoeken (naam, bedrijfsnaam, tussenvoegsel… — overleden: grijs in de lijst)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
          <select
            className="filter-select"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
          >
            <option value="">Alle groepen</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          {isAlleContacten && (
            <select
              className="filter-select"
              aria-label="Filter op type adresboek"
              value={entityKindFilter}
              onChange={(e) => setEntityKindFilter(e.target.value)}
            >
              <option value="">Alle typen</option>
              <option value="personeel">Personeel</option>
              <option value="vrijwilliger">Vrijwilliger</option>
              <option value="dominicusganger">Dominicusganger</option>
              <option value="extern">Extern contact</option>
              <option value="leverancier">Leverancier</option>
            </select>
          )}
          <input
            type="text"
            className="filter-select"
            style={{ minWidth: "120px" }}
            placeholder="Woonplaats bevat…"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            aria-label="Filter op plaats"
          />
          <input
            type="text"
            className="filter-select"
            style={{ minWidth: "120px" }}
            placeholder="Land bevat…"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            aria-label="Filter op land"
          />
          <select
            className="filter-select"
            aria-label="Filter op e-mailadres"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value as "" | "without" | "with")}
          >
            <option value="">E-mail: alle</option>
            <option value="without">Zonder e-mail</option>
            <option value="with">Met e-mail</option>
          </select>
          {isStaffAddressList && canFilterNoGroupActivity && (
            <select
              className="filter-select"
              aria-label="Filter op groepsrol"
              title="Filter op lidmaatschap of andere rol in een groep"
              value={staffGroupActivityFilter}
              onChange={(e) =>
                setStaffGroupActivityFilter(e.target.value as "all" | "none" | "with_role")
              }
            >
              <option value="all">Alle — geen filter op groep</option>
              <option value="with_role">Met groepsrol</option>
              <option value="none">Zonder groepsrol</option>
            </select>
          )}
          {canBulkDemoteVrijwilligers && (
            <button
              type="button"
              className="btn-secondary"
              disabled={demoteBusy || loading}
              onClick={() => void handleDemoteCurrentPageWithoutRoles()}
              title="Verwerkt alle kaarten op deze pagina zonder groepsrol: primair vrijwilliger → dominicusganger; met beide labels (vrijwilliger+dominicusganger) → primair dominicusganger; met alleen vrijwilliger-label → label verwijderd."
            >
              {demoteBusy ? "Bezig…" : "→ Dominicusgangers (pagina)"}
            </button>
          )}
          {canDemoteAllWithoutGroup && (
            <button
              type="button"
              className="btn-secondary"
              disabled={demoteBusy || loading}
              onClick={() => void openDemoteAllModal()}
              title="Alle vrijwilligers op de lijst zonder enige groepsrol (lid, voorzitter, beheer, kijker) naar dominicusgangers."
            >
              Opruimen: zonder groep
            </button>
          )}
        </div>
        <div className="filters-end">
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      {loading ? (
        viewMode === "tiles" ? (
          <div className="loading-grid">
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton-card" />)}
          </div>
        ) : (
          <div className="loading-list">
            {[...Array(8)].map((_, i) => <div key={i} className="skeleton-card" />)}
          </div>
        )
      ) : total === 0 ? (
        <div className="empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <p>Geen {pluralLabel}</p>
          {!readOnly && (
            <button className="btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
              Eerste {singularLabel} toevoegen
            </button>
          )}
        </div>
      ) : (
        <>
          {contacts.length === 0 ? (
            <div className="empty-state">
              <p>Geen resultaten op deze pagina. Wijzig filters of ga naar de eerste pagina.</p>
              <button type="button" className="btn-secondary" onClick={() => setPage(1)}>
                Eerste pagina
              </button>
            </div>
          ) : viewMode === "tiles" ? (
            <div className="contacts-grid">
              {contacts.map((contact) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  primaryListLabel={isAlleContacten ? primaryListBadgeLabel(contact.entity_kind) : undefined}
                  onView={() => setViewing(contact)}
                  onEdit={readOnly ? undefined : () => { setEditing(contact); setShowForm(true); }}
                  onDelete={readOnly ? undefined : () => handleDelete(contact.id)}
                  onAddToSendList={
                    vcard.sendListOpen ? () => vcard.addToSendList(contact) : undefined
                  }
                />
              ))}
            </div>
          ) : (
            <div className="data-list">
              {contacts.map((contact) => (
                <ContactListRow
                  key={contact.id}
                  contact={contact}
                  primaryListLabel={isAlleContacten ? primaryListBadgeLabel(contact.entity_kind) : undefined}
                  onView={() => setViewing(contact)}
                  onEdit={readOnly ? undefined : () => { setEditing(contact); setShowForm(true); }}
                  onDelete={readOnly ? undefined : () => handleDelete(contact.id)}
                  onAddToSendList={
                    vcard.sendListOpen ? () => vcard.addToSendList(contact) : undefined
                  }
                />
              ))}
            </div>
          )}
          <PaginationBar
            page={page}
            perPage={perPage}
            total={total}
            itemLabel={pluralLabel}
            onPageChange={setPage}
            onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
          />
        </>
      )}
        </div>

        <VcardEmailAside b={vcard} />
      </div>

      {viewing && (
        <ContactViewModal
          contact={viewing}
          user={user}
          apiPath={apiPath}
          title={singularLabel.charAt(0).toUpperCase() + singularLabel.slice(1)}
          showEditButton={!readOnly}
          onClose={() => {
            setViewing(null);
            if (searchParams.get("contactId")) {
              const next = new URLSearchParams(searchParams);
              next.delete("contactId");
              setSearchParams(next, { replace: true });
            }
          }}
          onEdit={
            readOnly
              ? undefined
              : (c) => {
                  setViewing(null);
                  if (searchParams.get("contactId")) {
                    const next = new URLSearchParams(searchParams);
                    next.delete("contactId");
                    setSearchParams(next, { replace: true });
                  }
                  setEditing(c);
                  setShowForm(true);
                }
          }
        />
      )}

      {showForm && (
        <ContactFormModal
          apiPath={apiPath}
          titleNieuw={`Nieuwe ${singularLabel}`}
          titleBewerken={`${singularLabel.charAt(0).toUpperCase() + singularLabel.slice(1)} bewerken`}
          contact={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchContacts(); }}
        />
      )}

      {demoteAllOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,10,46,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
          onClick={() => !demoteBusy && setDemoteAllOpen(false)}
        >
          <div
            className="card"
            style={{ maxWidth: 480, width: "100%", maxHeight: "80vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>Vrijwilligers zonder groep</h2>
            {demoteAllLoading ? (
              <p>Laden…</p>
            ) : demoteAllPreview.length === 0 ? (
              <p>Geen vrijwilligers zonder groepsrol gevonden.</p>
            ) : (
              <>
                <p>
                  {demoteAllPreview.length} personen staan op de vrijwilligerslijst maar hebben geen rol in
                  een groep. Ze worden dominicusganger (of het vrijwilliger-label wordt verwijderd).
                </p>
                <ul style={{ maxHeight: 240, overflow: "auto" }}>
                  {demoteAllPreview.map((p) => (
                    <li key={p.id}>{p.display_name}</li>
                  ))}
                </ul>
              </>
            )}
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
              <button type="button" className="btn-secondary" disabled={demoteBusy} onClick={() => setDemoteAllOpen(false)}>
                Annuleren
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={demoteBusy || demoteAllPreview.length === 0}
                onClick={() => void handleDemoteAllWithoutGroup()}
              >
                {demoteBusy ? "Bezig…" : "Bevestigen"}
              </button>
            </div>
          </div>
        </div>
      )}

      <MailerLiteConnectModal
        open={mlConnectOpen}
        onClose={() => setMlConnectOpen(false)}
        listTitle={title}
        entityKind={entityKindForMailerLite}
        filters={{
          search,
          group_id: groupFilter,
          city: cityFilter,
          country: countryFilter,
          entity_kind: entityKindFilter,
          group_activity: staffGroupActivityFilter,
        }}
        onSuccess={({ connection, push }) => {
          const pushInfo = push
            ? ` ${push.synced} gesynchroniseerd, ${push.skipped} overgeslagen.`
            : "";
          setMlConnectMsg(`MailerLite-groep “${connection.ml_group_name}” is gekoppeld.${pushInfo}`);
          void fetchMailerLiteListConnections();
        }}
      />

      {mlConnectMsg && (
        <p className="form-hint" style={{ marginTop: "1rem", color: "var(--color-success, #0a7)" }}>
          {mlConnectMsg}{" "}
          <button type="button" className="btn-link" onClick={() => setMlConnectMsg(null)}>
            Sluiten
          </button>
        </p>
      )}

    </div>
  );
}
